import { css } from '@compiled/react';
import type { FC } from 'react';
import React, { memo, useCallback, useEffect, useMemo, useContext } from 'react';
import { useIntl } from 'react-intl-next';

import type { ADNode } from '@atlaskit/editor-common/validator';
import type { DocNode } from '@atlaskit/adf-schema';
import type { ExtensionHandlers, ExtensionParams } from '@atlaskit/editor-common/extensions';
import { RendererActionsContext } from '@atlaskit/renderer/actions';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ValidationContextProvider } from '@atlaskit/renderer';
import { getPageElementCounts } from '@atlaskit/editor-common/utils';

import { useRendererAnnotationProviderOptions } from '@confluence/annotation-provider/entry-points/useRendererAnnotationProviderOptions';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey';
import { useContentType, usePageContentId } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer';
import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext';
import { useEditorContentModeValue } from '@confluence/editor-content-mode-button/entry-points/useEditorContentModeValue';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';

import type { LegacyContentMacroParams } from './LegacyContentMacroParams';

export interface NBMRendererProps {
	node: ExtensionParams<LegacyContentMacroParams>;
}

const DEFAULT_DOCUMENT: DocNode = {
	version: 1,
	type: 'doc',
	content: [
		{
			type: 'paragraph',
			content: [],
		},
	],
};

const collapsedEmptyLists = css({
	// Hide the bullets for deeply nested lists without sibling content so that they instead appear as if they are indented (a common formatting strategy in legacy editor content)
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
	'& li:has(> ul:only-child), & li:has(> ol:only-child)': {
		listStyleType: 'none',
		paddingLeft: 0,
	},
});

// Remove this wrapper when cleaning up feature gate `platform_editor_lce_collapse_bullets` and wrap with the div directly instead
const GatedStyleWrapper = ({ children }: { children: React.ReactNode }) => {
	if (fg('platform_editor_lce_collapse_bullets')) {
		return <div css={[collapsedEmptyLists]}>{children}</div>;
	}
	return children;
};

export const NBMRenderer: FC<NBMRendererProps> = memo(({ node }) => {
	const spaceId = useSpaceId();
	const spaceKey = useSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType = 'page'] = useContentType();
	const { environment, cloudId, userId, activationId } = useSessionData();
	const { getExtensionHandlers, populateExtensionProviders } = useContext(RendererExtensionContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const { mode: editorContentMode } = useEditorContentModeValue({ contentId });

	const document = node?.parameters?.nestedContent || DEFAULT_DOCUMENT;
	const appearance =
		expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
		expValEquals('confluence_max_width_content_appearance', 'isEnabled', true)
			? 'max'
			: 'full-width';

	/**
	 * Both `getExtensionHandlers` & `populateExtensionProviders` don't type check properly because in
	 * `confluence/next/packages/content-renderer-extension-context/src/RendererExtensionContext.tsx`
	 * the `options` is set to `any`. The reason most like is because the type lives inside classic codebase, here:
	 * `confluence/packages/confluence-fabric-editor/src/lib/renderer-extensions/renderer-macro-extension-handler.tsx`
	 */
	const extensionHandlers = useMemo(
		() =>
			getExtensionHandlers?.(
				{
					adf: document as ADNode,
					contentId,
					spaceKey,
					contentType,
					// This has not yet been set to adopt 'max' appearance by default as it will not have been adopted by extensions
					// instead setting to the largest supported width.
					// This will need to be revisited for "max width" GA release. (https://home.atlassian.com/o/2346a038-3c8c-498b-a79b-e7847859868d/s/a436116f-02ce-4520-8fbb-7301462a1674/project/ATLAS-107792/about)
					appearance: 'full-width',
					referentialityDataSourceProvider: {},
				},
				createAnalyticsEvent,
			),
		[contentId, contentType, createAnalyticsEvent, document, getExtensionHandlers, spaceKey],
	);

	const annotationProvider = useRendererAnnotationProviderOptions({
		pageId: contentId ?? '',
		validAnnotationsCallback: () => {},
		triggerAnalyticsEvent: createAnalyticsEvent,
		updateDocument: () => {},
		isArchived: false,
	});

	const getDataProviders = useCallback(() => {
		const providerFactory = ProviderFactory.create({});

		if (populateExtensionProviders) {
			populateExtensionProviders(providerFactory, {
				activationId,
				accountId: userId,
				cloudId,
				containerId: spaceKey,
				contentId,
				contentType,
				createAnalyticsEvent,
				environment,
				extensionHandlers,
				intl,
				spaceKey,
				spaceId,
			});
		}

		return providerFactory;
	}, [
		populateExtensionProviders,
		activationId,
		userId,
		cloudId,
		spaceKey,
		contentId,
		contentType,
		createAnalyticsEvent,
		environment,
		extensionHandlers,
		intl,
		spaceId,
	]);

	const validationContextValue = useMemo(() => ({ skipValidation: true }), []);

	useEffect(() => {
		if (contentId && createAnalyticsEvent && document)
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'rendererInitialised',
					actionSubject: 'legacyContentMacro',
					source: 'legacyContentMacro',
					attributes: {
						objectId: contentId,
						pageElementCounts: getPageElementCounts(document),
					},
				},
			}).fire();
	}, [contentId, createAnalyticsEvent, document, node.localId]);

	return (
		<ValidationContextProvider value={validationContextValue}>
			<RendererActionsContext>
				<GatedStyleWrapper>
					<ADFRenderer
						annotationProvider={annotationProvider}
						allowAnnotations
						appearance={appearance}
						allowHeadingAnchorLinks={false}
						contentId={contentId}
						dataProviders={getDataProviders()}
						document={document}
						extensionHandlers={
							fg('platform_editor_lce_dataprovider-only_handlers')
								? undefined
								: (extensionHandlers as unknown as ExtensionHandlers)
						}
						contentMode={editorContentMode}
					/>
				</GatedStyleWrapper>
			</RendererActionsContext>
		</ValidationContextProvider>
	);
});
